import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Sparkles, Phone, Loader2, ShieldCheck } from "lucide-react";

/**
 * Shown to Google-signup students on their first dashboard visit — they don't
 * have a phone number yet and would fail the booking flow later. Non-dismissible
 * on save; skippable but reappears next visit until phone is filled.
 */
export default function CompleteProfileNudge() {
  const { user, setUser } = useAuth();

  // Show when: student role, no phone yet. Owners already provided phone at signup.
  const shouldShow =
    user &&
    user.role === "student" &&
    (!user.phone || user.phone.length < 10) &&
    !sessionStorage.getItem("hn_profile_nudge_dismissed");

  const [open, setOpen] = useState(!!shouldShow);
  const [phone, setPhone] = useState(user?.phone || "");
  const [gender, setGender] = useState(user?.gender || "");
  const [saving, setSaving] = useState(false);

  if (!shouldShow) return null;

  const save = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) return toast.error("Phone must be a 10-digit Indian number");
    if (!gender) return toast.error("Please pick a gender so we can show you the right hostels");
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { phone: digits, gender });
      localStorage.setItem("hn_user", JSON.stringify(data));
      setUser?.(data);
      toast.success("Profile complete! You can now book any hostel.");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    sessionStorage.setItem("hn_profile_nudge_dismissed", "1");
    setOpen(false);
    toast("You can add these later from your profile 👋");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && skip()}>
      <DialogContent
        data-testid="complete-profile-nudge"
        className="max-w-md w-[calc(100%-20px)] rounded-2xl top-[50px] translate-y-0 max-h-[calc(100dvh-70px)] sm:top-[50%] sm:translate-y-[-50%] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles size={18} className="text-turmeric-500" />
            One quick step, {user.name?.split(" ")[0]}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          You signed up with Google — we just need <b>two things</b> before you can book a hostel:
        </p>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5"><Phone size={12} /> WhatsApp phone number</label>
          <Input
            data-testid="nudge-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="numeric"
            maxLength={12}
            placeholder="98765 43210"
            className="mt-1 h-11"
          />
          <div className="text-[11px] text-slate-500 mt-1">Your booking confirmation, verification code, and owner contact are sent to this number.</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Gender</label>
          <RadioGroup value={gender} onValueChange={setGender} className="mt-2 grid grid-cols-2 gap-2">
            {[
              { v: "male", l: "Male" },
              { v: "female", l: "Female" },
              { v: "other", l: "Other" },
              { v: "prefer_not_to_say", l: "Prefer not to say" },
            ].map((o) => (
              <label
                key={o.v}
                data-testid={`nudge-gender-${o.v}`}
                className={`cursor-pointer flex items-center gap-2 border-2 rounded-lg px-3 py-2 text-sm ${gender === o.v ? "border-vermilion-600 bg-vermilion-50 font-semibold" : "border-slate-200"}`}
              >
                <RadioGroupItem value={o.v} className="w-4 h-4" />
                {o.l}
              </label>
            ))}
          </RadioGroup>
          <div className="text-[11px] text-slate-500 mt-1">Used only to show hostels that accept your gender (boys/girls/coed).</div>
        </div>

        <div className="rounded-lg bg-jodhpur-50 border border-jodhpur-200 px-3 py-2 flex gap-2 text-[11px] text-jodhpur-900">
          <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-jodhpur-700" />
          <span>Aadhaar KYC is required only before you make a booking — you can complete that later from the KYC page.</span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={skip} disabled={saving} data-testid="nudge-skip-btn">Skip for now</Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="nudge-save-btn"
            className="bg-vermilion-600 hover:bg-vermilion-700 text-white gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {saving ? "Saving…" : "Save & continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
